import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useBuilderStore } from '../../stores/builderStore';
import { Search, Plus, Star, Layout, Type, Image, Video, Grid, MessageSquare, ArrowRight, Zap, X, Sparkles, Crown } from 'lucide-react';
import { premiumTemplates, templateCategories } from './premiumTemplates';

export const COMPONENT_LIBRARY = {
  sections: {
    hero: {
      name: 'Hero Banner',
      icon: Layout,
      category: 'Layout',
      description: 'Eye-catching banner section',
      variants: [
        {
          id: 'hero_center',
          name: 'Centered Hero',
          preview: '🎯',
          config: {
            type: 'hero',
            props: {
              title: 'Welcome to Our Platform',
              subtitle: 'Create amazing experiences for your users',
              buttonText: 'Get Started',
              buttonLink: '#',
              backgroundImage: '',
              height: '600px',
              alignment: 'center'
            },
            styles: {
              backgroundColor: '#1e40af',
              textColor: '#ffffff',
              overlayOpacity: 0.5
            }
          }
        },
        {
          id: 'hero_split',
          name: 'Split Screen',
          preview: '⬛⬜',
          config: {
            type: 'hero_split',
            props: {
              title: 'Build Something Amazing',
              subtitle: 'Professional tools for modern teams',
              buttonText: 'Start Free Trial',
              image: '',
              imagePosition: 'right'
            }
          }
        },
        {
          id: 'hero_video',
          name: 'Video Background',
          preview: '📹',
          config: {
            type: 'hero_video',
            props: {
              title: 'Experience Excellence',
              subtitle: 'Watch our story unfold',
              videoUrl: '',
              autoplay: true,
              muted: true
            }
          }
        }
      ]
    },
    features: {
      name: 'Features',
      icon: Star,
      category: 'Content',
      description: 'Showcase your features',
      variants: [
        {
          id: 'features_3col',
          name: '3 Column Grid',
          preview: '⚡⚡⚡',
          config: {
            type: 'features',
            props: {
              title: 'Our Features',
              columns: 3,
              variant: 'netflix', // Default to Netflix style for horizontal scrolling
              items: [
                { image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400', title: 'Lightning Fast', description: 'Optimized for speed and performance', link: '/features' },
                { image: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=400', title: 'Beautiful Design', description: 'Stunning visuals that captivate', link: '/design' },
                { image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400', title: 'Mobile Ready', description: 'Perfect on every device', link: '/mobile' }
              ]
            }
          }
        },
        {
          id: 'features_zigzag',
          name: 'Zigzag Layout',
          preview: '↗↙',
          config: {
            type: 'features_zigzag',
            props: {
              items: [
                {
                  title: 'Advanced Analytics',
                  description: 'Get detailed insights into your performance',
                  image: '',
                  imagePosition: 'left'
                },
                {
                  title: 'Team Collaboration',
                  description: 'Work together seamlessly',
                  image: '',
                  imagePosition: 'right'
                }
              ]
            }
          }
        },
        {
          id: 'features_cards',
          name: 'Feature Cards',
          preview: '🃏',
          config: {
            type: 'features_cards',
            props: {
              title: 'Why Choose Us',
              cards: [
                {
                  icon: '🚀',
                  title: 'Fast Deployment',
                  description: 'Get started in minutes',
                  link: '#'
                }
              ]
            }
          }
        }
      ]
    },
    testimonials: {
      name: 'Testimonials',
      icon: MessageSquare,
      category: 'Social Proof',
      description: 'Customer testimonials',
      variants: [
        {
          id: 'testimonials_carousel',
          name: 'Carousel',
          preview: '💬',
          config: {
            type: 'testimonials_carousel',
            props: {
              title: 'What Our Customers Say',
              autoplay: true,
              interval: 5000,
              items: [
                {
                  quote: 'This product changed our business',
                  author: 'John Doe',
                  role: 'CEO at Company',
                  avatar: ''
                }
              ]
            }
          }
        },
        {
          id: 'testimonials_grid',
          name: 'Grid Layout',
          preview: '⭐⭐⭐',
          config: {
            type: 'testimonials_grid',
            props: {
              title: 'Customer Reviews',
              columns: 3,
              items: []
            }
          }
        }
      ]
    },
    cta: {
      name: 'Call to Action',
      icon: ArrowRight,
      category: 'Conversion',
      description: 'Drive user action',
      variants: [
        {
          id: 'cta_simple',
          name: 'Simple CTA',
          preview: '→',
          config: {
            type: 'cta',
            props: {
              title: 'Ready to get started?',
              subtitle: 'Join thousands of satisfied customers today',
              buttonText: 'Start Free Trial',
              buttonLink: '/signup',
              secondaryButtonText: 'Learn More',
              secondaryButtonLink: '/features'
            },
            styles: {
              backgroundColor: '#f3f4f6',
              accentColor: '#3b82f6'
            }
          }
        },
        {
          id: 'cta_newsletter',
          name: 'Newsletter Signup',
          preview: '📧',
          config: {
            type: 'cta_newsletter',
            props: {
              title: 'Stay Updated',
              subtitle: 'Get the latest news and updates',
              placeholder: 'Enter your email',
              buttonText: 'Subscribe'
            }
          }
        }
      ]
    },
    gallery: {
      name: 'Gallery',
      icon: Image,
      category: 'Media',
      description: 'Image galleries',
      variants: [
        {
          id: 'gallery_grid',
          name: 'Grid Gallery',
          preview: '🖼️',
          config: {
            type: 'gallery',
            props: {
              title: 'Our Work',
              columns: 4,
              images: []
            }
          }
        },
        {
          id: 'gallery_masonry',
          name: 'Masonry Layout',
          preview: '🧱',
          config: {
            type: 'gallery_masonry',
            props: {
              images: []
            }
          }
        }
      ]
    },
    stats: {
      name: 'Statistics',
      icon: Zap,
      category: 'Data',
      description: 'Show impressive numbers',
      variants: [
        {
          id: 'stats_counter',
          name: 'Counter Stats',
          preview: '123',
          config: {
            type: 'stats',
            props: {
              items: [
                { number: '1000+', label: 'Happy Customers' },
                { number: '99%', label: 'Uptime' },
                { number: '24/7', label: 'Support' },
                { number: '50+', label: 'Countries' }
              ]
            }
          }
        }
      ]
    },
    pricing: {
      name: 'Pricing',
      icon: Grid,
      category: 'Commerce',
      description: 'Pricing tables',
      variants: [
        {
          id: 'pricing_table',
          name: 'Pricing Table',
          preview: '💰',
          config: {
            type: 'pricing',
            props: {
              title: 'Choose Your Plan',
              plans: [
                {
                  name: 'Starter',
                  price: '$9',
                  period: 'month',
                  features: ['Feature 1', 'Feature 2'],
                  buttonText: 'Choose Plan',
                  highlighted: false
                },
                {
                  name: 'Pro',
                  price: '$29',
                  period: 'month',
                  features: ['All Starter features', 'Advanced features'],
                  buttonText: 'Choose Plan',
                  highlighted: true
                }
              ]
            }
          }
        }
      ]
    },
    quickActions: {
      name: 'Quick Actions',
      icon: Zap,
      category: 'Navigation',
      description: 'Quick action buttons grid',
      variants: [
        {
          id: 'quick_actions_4',
          name: '4 Action Buttons',
          preview: '⚡',
          config: {
            type: 'quick_actions',
            props: {
              title: 'Quick Start',
              subtitle: 'Jump right into learning',
              actions: [
                { icon: '📚', label: 'Study Materials' },
                { icon: '📝', label: 'Mock Tests' },
                { icon: '📊', label: 'Analytics' },
                { icon: '💬', label: 'Discussions' }
              ]
            }
          }
        },
        {
          id: 'quick_actions_6',
          name: '6 Action Buttons',
          preview: '⚡⚡',
          config: {
            type: 'quick_actions',
            props: {
              title: 'Quick Access',
              subtitle: 'Your most used features',
              actions: [
                { icon: '📚', label: 'Library' },
                { icon: '📝', label: 'Tests' },
                { icon: '📊', label: 'Reports' },
                { icon: '💬', label: 'Chat' },
                { icon: '⚙️', label: 'Settings' },
                { icon: '❓', label: 'Help' }
              ]
            }
          }
        },
        {
          id: 'quick_actions_custom',
          name: 'Custom Actions',
          preview: '✨',
          config: {
            type: 'quick_actions',
            props: {
              title: 'Services',
              subtitle: '',
              actions: [
                { icon: '🚀', label: 'Get Started' },
                { icon: '📖', label: 'Documentation' },
                { icon: '🛠️', label: 'Tools' }
              ]
            }
          }
        }
      ]
    }
  },
  blocks: {
    text: {
      name: 'Text Block',
      icon: Type,
      config: {
        props: {
          blocks: [
            {
              type: 'h1',
              content: 'Your Heading Here',
              fontSize: 32,
              fontWeight: 'bold',
              textAlign: 'left',
              color: '#000000',
              fontFamily: 'default'
            },
            {
              type: 'p',
              content: 'Add your paragraph text here. You can customize font size, weight, alignment and color for each block.',
              fontSize: 16,
              fontWeight: 'normal',
              textAlign: 'left',
              color: '#374151',
              fontFamily: 'default'
            }
          ]
        }
      }
    },
    image: {
      name: 'Image',
      icon: Image,
      config: {
        props: {
          src: '',
          alt: '',
          width: '100%',
          height: 'auto'
        }
      }
    },
    button: {
      name: 'Button',
      icon: ArrowRight,
      config: {
        props: {
          text: 'Click Me',
          link: '#',
          style: 'primary',
          size: 'medium'
        }
      }
    },
    video: {
      name: 'Video',
      icon: Video,
      config: {
        props: {
          url: '',
          autoplay: false,
          controls: true
        }
      }
    },
    spacer: {
      name: 'Spacer',
      icon: Layout,
      config: {
        props: {
          height: '50px'
        }
      }
    }
  }
};

interface ComponentLibraryProps {
  onClose?: () => void;
}

export const ComponentLibrary: React.FC<ComponentLibraryProps> = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showPremium, setShowPremium] = useState(false);
  const addSection = useBuilderStore(state => state.addSection);

  const categories = ['all', 'Layout', 'Content', 'Navigation', 'Social Proof', 'Conversion', 'Media', 'Data', 'Commerce'];

  const handleAddSection = (variant: any, sectionKey: string) => {
    // Pass the entire config as the template
    addSection(variant.config.type, variant.config);
    onClose?.();
  };

  const handleAddBlock = (blockKey: string, block: any) => {
    console.log('handleAddBlock called with:', blockKey, block);

    // Convert block config to section config
    const sectionConfig = {
      type: blockKey, // Use the block key as the section type (text, image, button, etc.)
      props: block.config.props || {},
      styles: block.config.styles || {},
      title: block.name
    };

    console.log('Creating section with config:', sectionConfig);
    addSection(blockKey, sectionConfig);
    onClose?.();
  };

  const filteredSections = Object.entries(COMPONENT_LIBRARY.sections).filter(([key, section]) => {
    const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          section.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || section.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="w-80 bg-white border-r h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg">Components</h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Hide sidebar"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mt-3">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                activeCategory === cat
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Components List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredSections.map(([key, section]) => {
          const Icon = section.icon;
          return (
            <div key={key} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-gray-600" />
                <h4 className="font-medium">{section.name}</h4>
              </div>
              <p className="text-sm text-gray-500 mb-3">{section.description}</p>

              {/* Variants */}
              <div className="space-y-2">
                {section.variants.map(variant => (
                  <button
                    key={variant.id}
                    onClick={() => handleAddSection(variant, key)}
                    className="w-full p-3 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors group text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{variant.preview}</span>
                        <span className="text-sm font-medium">{variant.name}</span>
                      </div>
                      <Plus className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {/* Blocks */}
        <div className="mt-6 pt-6 border-t">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Grid className="w-5 h-5 text-gray-600" />
            Basic Blocks
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(COMPONENT_LIBRARY.blocks).map(([key, block]) => {
              const Icon = block.icon;
              return (
                <button
                  key={key}
                  onClick={() => handleAddBlock(key, block)}
                  className="p-3 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  title={`Add ${block.name}`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                  <span className="text-xs">{block.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};